import { useIsTemplateQualityGateKilled } from '@/features/flags/template-quality-gate-flag';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import { trackTemplateQualityGate } from '@/models/analytics/features/trackTemplateQualityGateAnalytics';
import { logger } from '@/utils/logger/logger';
import {
  templateQualityGateResolverAtom,
  type TemplateQualityGateContext,
  type TemplateQualityGateDecision,
  type TemplateQualityGateRegionStatus,
  type TemplateQualityGateSurface,
} from '@heidi/templates';
import { willTemplateSaveCreate, type TemplateSavePlan } from '@heidi/templates/data';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

export interface TemplateQualityGate {
  /** False outside UK/EU or when the kill switch is on; no save is ever gated. */
  isEnabled: boolean;
  /**
   * Whether this market mandates the attestation, before the kill switch is
   * applied. Separate from `isEnabled` so a suppressed save can be told from
   * one that was never in scope.
   */
  isInScope: boolean;
  /** The conditions this gate evaluates under, for reporting on its decisions. */
  context: TemplateQualityGateContext;
  /** Opens the gate dialog and suspends until the clinician decides. */
  requireAcceptance: (templateId?: string) => Promise<TemplateQualityGateDecision>;
}

/**
 * Resolves whether a save may proceed, prompting for the attestation first when
 * the save would create a template. False means the save must unwind.
 *
 * Outside the hook so the branching is unit-testable without rendering a
 * template editor, and so both builders share one decision.
 */
export const resolveTemplateQualityGate = async (
  gate: TemplateQualityGate,
  savePlan: TemplateSavePlan,
  templateId?: string
): Promise<boolean> => {
  if (!willTemplateSaveCreate(savePlan)) {
    return true;
  }

  const context = { ...gate.context, templateId };

  if (!gate.isEnabled) {
    // Only a save the kill switch suppressed is worth reporting — it means the
    // control is off in a market that mandates it. An out-of-region save was
    // never in scope, so reporting it would say nothing while outnumbering the
    // gated traffic several times over and burying the switch.
    //
    // `isInScope` inherits the fail-open of `useIsUKOrEU`, so a save made
    // before the region query settles counts as in scope wherever the user
    // really is. Read `bypassed` alongside `region_status: resolved` to get
    // the mandated markets only.
    if (gate.isInScope) {
      trackTemplateQualityGate('bypassed', context);
    }
    return true;
  }

  if (context.regionStatus === 'error') {
    logger.warn('template_quality_gate_region_lookup_failed', {
      surface: context.surface,
      templateId,
    });
  }

  const decision = await gate.requireAcceptance(templateId);
  trackTemplateQualityGate(decision, context);
  return decision === 'accepted';
};

/**
 * Gate for the template quality attestation (SWR-185).
 *
 * The attestation is required on **every** save that creates a bespoke
 * template, not once per clinician. The requirement reads "on click of save for
 * a generated template", and a once-per-clinician variant was considered and
 * rejected: it weakens an approved risk control, because a clinician who
 * attested months ago never re-confirms that *this* template was reviewed.
 * There is therefore no persisted acceptance state to read or write — the
 * decision is per-save and lives entirely in this session.
 *
 * `requireAcceptance` resolves through a Jotai-held resolver rather than
 * returning control to the caller, so the save that triggered it stays
 * suspended mid-flight — callers that await the created template id (the Share
 * button) keep working unchanged.
 *
 * The attestation is only mandated in the regulated UK/EU markets, so the gate
 * is region-scoped on top of the kill switch. `useIsUKOrEU` reads the
 * authoritative Kinde region — never `getRegion()`, which fails open to AU and
 * honours the spoofable `?region=` param, and so must not gate a
 * medical-device decision. Its pending/errored state is deliberately consumed
 * as "in region": the gate runs inside a save the clinician just triggered, and
 * an unresolved region must prompt rather than wave the save through.
 *
 * `surface` is per hook instance rather than per call because one component
 * owns one entry point; the template id varies save to save and is passed in.
 */
export const useTemplateQualityGate = (
  surface: TemplateQualityGateSurface
): TemplateQualityGate => {
  const { isUKOrEU, isPending, isError, region } = useIsUKOrEU();
  // Read unconditionally: `&&` on the hook call itself would skip it whenever
  // the region check is false and break the hook order between renders.
  const isKilled = useIsTemplateQualityGateKilled();
  const isInScope = isUKOrEU;
  const isEnabled = isInScope && !isKilled;
  const setResolver = useSetAtom(templateQualityGateResolverAtom);

  const regionStatus: TemplateQualityGateRegionStatus = isError
    ? 'error'
    : isPending
      ? 'pending'
      : 'resolved';

  const context = useMemo<TemplateQualityGateContext>(
    () => ({ surface, userRegion: region, regionStatus, isKillSwitchOn: isKilled }),
    [surface, region, regionStatus, isKilled]
  );

  const requireAcceptance = useCallback(
    (templateId?: string) =>
      new Promise<TemplateQualityGateDecision>((resolve) => {
        setResolver((pending) => {
          if (pending) {
            // A second save must never orphan the first: unwind it instead of
            // leaving its promise — and the save awaiting it — pending
            // forever. `superseded`, not `declined`: nobody declined anything.
            //
            // Warned because it destroys a clinician's in-flight save, and
            // Datadog is where support looks when someone reports one
            // vanishing. The event itself is emitted by the unwound save's own
            // `resolveTemplateQualityGate`, which holds its context.
            logger.warn('template_quality_gate_save_superseded', {
              surface: pending.context.surface,
              templateId: pending.context.templateId,
            });
            pending.resolve('superseded');
          }
          return { resolve, context: { ...context, templateId } };
        });
      }),
    [setResolver, context]
  );

  // Stable identity so callers can list the gate as one dependency.
  return useMemo(
    () => ({ isEnabled, isInScope, context, requireAcceptance }),
    [isEnabled, isInScope, context, requireAcceptance]
  );
};
